import React, {useEffect, useMemo, useState} from 'react';
import {HiCheckCircle, HiChevronDown, HiX} from 'react-icons/hi';
import {useJwt} from '../../helpers/common';
import {useOrganizationService} from '../../services';
import {useDispatch, useSelector} from 'react-redux';
import useIntegrationStatus from '../../hooks/useIntegrationStatus';
import {OnboardingStepKeyEnum, PMTOOLS_PROVIDER_ORDER, useOnboardingNavigator} from '../Onboarding';
import {DashboardStatus} from '../../constants/integrationTypes';
import { setOrganizationOnboardSettings } from '../../redux/slices/organization';
import { useLocation } from 'react-router';
import { useSmapFetch as useStrategyMapFetch } from '../../hooks/Smap/useSmap';

const TourGuideComponent = () => {
  const jwtData:any = useJwt()
  const location = useLocation();
  const isAdmin = typeof jwtData?.org_role === 'string' && String(jwtData?.org_role).toLowerCase() === 'admin';
  const [isInitialized, setIsInitialized] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const { updateOrganization, fetchOrgIntegrations, fetchOrganization } = useOrganizationService()
  const { onboardSettings = {} } = useSelector((state: any) => state.organization)
  // const { roadmaps = [] } = useSelector((state: any) => state.roadmap || {})
  const { data: strategyMaps = [], refreshStrategyMaps } =
    useStrategyMapFetch();

  const { isIntegrationActive } = useIntegrationStatus();
  const { goToStep } = useOnboardingNavigator();

  useEffect(() => {
    fetchOrgIntegrations();
    refreshStrategyMaps();
  }, []);

  useEffect(() => {
    const initOrg = async () => {
      if (jwtData?.org_id) {
        try {
          await fetchOrganization(jwtData.org_id);
          setIsInitialized(true);
        } catch (error) {
          console.error("Error fetching organization:", error);
        }
      }
    };
    initOrg();
  }, [jwtData?.org_id]);

  const handleClickableLink = async (key?: OnboardingStepKeyEnum) => {
    if (!jwtData?.org_id || !isInitialized) return;

    let next = onboardSettings || {};

    if (key) {
      if (onboardSettings?.dismissed === true) {
        next = { ...next, dismissed: false, current_navigation: key };
        await updateOrganization(jwtData.org_id, { onboard_settings: next }, true);
      }
      try { window.dispatchEvent(new CustomEvent('onboarding:start', { detail: { key } })); } catch {}

      const correspondingLocation = {
        [OnboardingStepKeyEnum.RiskMitigation]: "/strategymaps",
        [OnboardingStepKeyEnum.ImportTeams]: "/teams",
        [OnboardingStepKeyEnum.OrgSkills]: "/settings",
        [OnboardingStepKeyEnum.ImportUsers]: "/people",
        [OnboardingStepKeyEnum.PmTools]: "/settings",
        [OnboardingStepKeyEnum.AllocateSkills]: "/people",
        [OnboardingStepKeyEnum.Explore]: "/workitems",
        [OnboardingStepKeyEnum.OrgHolidays]: "/settings",
        [OnboardingStepKeyEnum.StrategyMap]: "/strategymaps",
        [OnboardingStepKeyEnum.Integrations]: "/settings",
        [OnboardingStepKeyEnum.InvitePeople]: "/people",
      };


      //@ts-ignore
      next = { ...next, current_navigation: key, current_location: String(correspondingLocation[key])};


      dispatch(setOrganizationOnboardSettings(next));
      goToStep(key);
      setIsOpen(false);
      return;
    }

    setIsOpen(false);
  }

  useEffect(() => {
    const onTourOpen = () => {
      setIsOpen(true);
    };
    window.addEventListener('tour-open', onTourOpen);
    return () => window.removeEventListener('tour-open', onTourOpen);
  }, []);

  // Collapse the tour guide if any onboarding tooltip opens; re-open remains user-driven
  useEffect(() => {
    const onTooltipOpen = () => setIsOpen(false);
    window.addEventListener('onboarding:tooltip:open', onTooltipOpen);
    return () => window.removeEventListener('onboarding:tooltip:open', onTooltipOpen);
  }, []);

  const handleCloseClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!jwtData?.org_id || !isInitialized) return;

    const next = { ...onboardSettings, close: true };

    dispatch(setOrganizationOnboardSettings(next));
    setIsOpen(false);

    await updateOrganization(jwtData.org_id, { onboard_settings: next }, true);
  };

  type StepItem = {
    id: number
    label: string
    description: string
    done: boolean
    onClick?: () => void
  }

  const hasAtRiskRoadmaps = useMemo(() => Array.isArray(strategyMaps) && strategyMaps.some((r: any) => String(r?.status || '').toLowerCase() === DashboardStatus.AT_RISK), [strategyMaps]);

  const steps: StepItem[] = useMemo(() => {
    const azureActive = isIntegrationActive('azure');
    const jiraActive = isIntegrationActive('jira');


    const stepsList: StepItem[] = [
      {
        id: 1,
        label: "Create An Org",
        done: true,
        onClick: undefined,
        description: "Your workspace is ready",
      },
      
    ];

     stepsList.push({
       id: stepsList.length + 1,
       label: "Work Items",
      //  done: true,
       done: onboardSettings?.explore,
       onClick: () => handleClickableLink(OnboardingStepKeyEnum.Explore),
       description: "Explore Work Item Types",
     });
     stepsList.push({
       id: stepsList.length + 1,
       label: "Strategy Maps",
      //  done: true,
        done: onboardSettings?.strategy_map,
       onClick: () => handleClickableLink(OnboardingStepKeyEnum.StrategyMap),
       description: "Explore Strategy Maps",
     });



    if (hasAtRiskRoadmaps) {
      stepsList.push({
        id: stepsList.length + 1,
        label: "Risk Mitigation",
        done: onboardSettings?.risk_mitigation?.awareness && onboardSettings?.risk_mitigation?.analyze,
        onClick: () => handleClickableLink(OnboardingStepKeyEnum.RiskMitigation),
        description: "Identify and mitigate risks"
      });
    }

    stepsList.push({
      id: stepsList.length + 1,
      label: "Integrate PM Tools",
      done: onboardSettings?.integrations,
      onClick: () => handleClickableLink(OnboardingStepKeyEnum.Integrations),
      description: "Connect Jira / Azure / more"
    });

    let nextId = stepsList.length + 1;

    // if (azureActive || jiraActive) {
    //   stepsList.push({
    //     id: nextId++,
    //     label: jiraActive ? "Import Users From Jira" : "Import Users From Azure DevOps",
    //     done: !!onboardSettings.import_users,
    //     onClick: () => handleClickableLink(OnboardingStepKeyEnum.ImportUsers),
    //     description: "Import users from your PM Tool"
    //   });
    // }
    
    stepsList.push({
      id: nextId++,
      label: (jiraActive || azureActive) ? `Import From ${jiraActive && azureActive ? "Jira & Azure DevOps" : jiraActive ? "Jira" : "Azure DevOps"}`: "Invite People",
      done: !!onboardSettings.invite_people,
      onClick: () => handleClickableLink(OnboardingStepKeyEnum.InvitePeople),
      description: (jiraActive || azureActive) ? `Manage your resources and imports` : "Invite people to your organization"
    });

    // if (azureActive) {
    //   stepsList.push({
    //     id: nextId++,
    //     label: "Import Teams From Azure DevOps",
    //     done: !!onboardSettings.import_teams,
    //     onClick: () => handleClickableLink(OnboardingStepKeyEnum.ImportTeams),
    //     description: "Import teams from your PM Tool"
    //   });
    // }

    stepsList.push(
      {
        id: nextId++,
        label: "Add Custom Skill",
        done: !!onboardSettings.org_skills,
        onClick: () => handleClickableLink(OnboardingStepKeyEnum.OrgSkills),
        description: "Manage skills in your organization"
      },
      {
        id: nextId++,
        label: "Allocate Skills",
        done: !!onboardSettings.allocate_skills,
        onClick: () => handleClickableLink(OnboardingStepKeyEnum.AllocateSkills),
        description: "Allocate skills to resources"
      },
      {
        id: nextId++,
        label: "Add Custom Holiday",
        done: !!onboardSettings.org_holidays,
        onClick: () => handleClickableLink(OnboardingStepKeyEnum.OrgHolidays),
        description: "Manage holidays of your organization"
      }
    );

    return stepsList;
  }, [isIntegrationActive, onboardSettings, hasAtRiskRoadmaps]);

  const progress = useMemo(() => {
    const completed = steps.filter(s => s.done).length;
    return Math.round((completed / steps.length) * 100);
  }, [steps]);

  const firstIncompleteIndex = useMemo(() => {
    return steps.findIndex(s => !s.done);
  }, [steps]);

  // Admin-only: hide tour completely for non-admins
  if (!isAdmin) return null;

  // Hide completely while onboarding settings are not loaded or user closed globally
  if (!isInitialized || onboardSettings?.close) return null;

  return (
    <div className="fixed z-[9999]" style={{ bottom: 42, right: document.getElementsByClassName("ai-chatbot").length === 0 ? 24 : 132 }}>
      {/*right 24px in case chatbot is visible*/}
      <div className="relative w-[28rem] rounded-2xl overflow-hidden shadow-lg border border-light-border-default/60 dark:border-slate-700/60 bg-secondary-light dark:bg-secondary-dark">
        <div
          className="bg-secondary-light dark:bg-secondary-dark px-5 py-4 w-full flex items-start gap-3 cursor-pointer"
          onClick={() => setIsOpen((v) => !v)}
        >
          <div className="flex-1">
            <div className="w-full text-left">
              <div className="flex flex-col items-start gap-2">
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-tertiary-light dark:bg-tertiary-dark text-light-text-primary dark:text-light-text-inverse ring-1 ring-inset ring-light-border-subtle/50">Getting Started</span>
                </div>
                <div className="ml-2 text-xs text-light-text-secondary dark:text-light-text-inverse/70">{progress}% completed</div>
              </div>
            </div>
            <div className="mt-3 ml-2">
              <div className="h-2 w-full rounded-full bg-tertiary-light dark:bg-tertiary-dark overflow-hidden ring-1 ring-inset ring-light-border-subtle/50">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary-light to-primary-hover dark:from-primary-dark dark:to-primary-dark transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
          <div className="flex items-start gap-2 pt-1">
            <div className="h-8 w-8 rounded-full border border-light-border-default/60 dark:border-slate-700/60 bg-secondary-light dark:bg-secondary-dark text-light-text-secondary dark:text-light-text-inverse/70 flex items-center justify-center shadow-sm hover:bg-hover-light/80 dark:hover:bg-hover-dark/60 transition">
              <span className={`${isOpen ? 'rotate-0' : '-rotate-90'} transition-transform`}>
                <HiChevronDown />
              </span>
            </div>
            <button
              aria-label="Close onboarding"
              className="h-8 w-8 rounded-full border border-light-border-default/60 dark:border-slate-700/60 bg-secondary-light dark:bg-secondary-dark text-light-text-secondary dark:text-light-text-inverse/70 flex items-center justify-center shadow-sm hover:bg-hover-light/80 dark:hover:bg-hover-dark/60"
              onClick={handleCloseClick}
            >
              <HiX />
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="p-5 bg-secondary-light dark:bg-secondary-dark border-t border-light-border-default/60 dark:border-slate-700/60">
            <div className="rounded-2xl ring-1 ring-inset ring-light-border-subtle/60 bg-tertiary-light/50 dark:bg-tertiary-dark/50">
              {steps.map((s: StepItem, idx: number) => (
                <div key={s.id} className={idx < steps.length - 1 ? 'border-b border-light-border-default/50 dark:border-slate-700/50' : ''}>
                  <StepRow
                    idx={idx}
                    label={s.label}
                    description={s.description}
                    firstIncompleteIndex={firstIncompleteIndex}
                    done={s.done}
                    onClick={s.onClick}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const StepRow = ({ idx, label, description, done, onClick, firstIncompleteIndex }: { idx: number; label: string; description: string; done: boolean; onClick?: () => void; firstIncompleteIndex: number }) => {
  const isCurrent = idx === firstIncompleteIndex
  return (
    <div className="relative">
      <div className={`flex items-start gap-3 rounded-xl px-3 py-3 transition-colors bg-transparent hover:bg-hover-light/60 dark:hover:bg-hover-dark/50`}>
        <div className="pt-1">
          {done ? (
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-500/15 text-green-500 ring-1 ring-green-500/30">
                <HiCheckCircle />
              </span>
          ) : (
            <span className={`flex h-6 w-6 items-center justify-center rounded-full ring-2 ${isCurrent ? 'ring-primary-light dark:ring-primary-dark' : 'ring-light-border-subtle/60'} bg-tertiary-light dark:bg-tertiary-dark text-xs text-light-text-secondary dark:text-light-text-inverse/80`}>{idx + 1}</span>
          )}
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-light-text-primary dark:text-light-text-inverse">{label}</div>
          <div className={`text-xs ${done ? 'line-through text-light-text-tertiary' : 'text-light-text-secondary'} dark:text-light-text-inverse/70`}>{description}</div>
        </div>
        <div className="self-center">
          {!done && (
            <button
              onClick={() => isCurrent && onClick?.()}
              // onClick={onClick}
              className={`rounded-full px-3 py-1 text-xs font-medium shadow-sm border transition-colors ${isCurrent ? 'bg-primary-light text-white dark:bg-primary-dark border-primary-hover' : 'cursor-not-allowed bg-tertiary-light dark:bg-tertiary-dark text-light-text-secondary dark:text-light-text-inverse/70 border-light-border-default/60 dark:border-slate-700/60'}`}
            >
              Start
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default TourGuideComponent;